import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite, type Role } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { number, thaiDateTime } from '../lib/format'

/**
 * Multi-agent orchestration with human checkpoints (part 68).
 *
 * The screen has three jobs:
 *
 *   1. Declare which capabilities each agent holds. The capability catalogue is a closed
 *      whitelist served by the API; the form cannot invent a capability.
 *   2. Start a goal. The API plans the DAG against the declared agents before writing anything,
 *      so an agent that lacks a required capability is rejected here, not discovered later.
 *   3. Inspect a run and approve its checkpoint. A checkpoint never completes by itself; the
 *      approval button is the only path and it records the signed-in user as the approver.
 *
 * A blocked run shows the failing step and the reason. Steps after it are explicitly skipped,
 * which is how the screen proves that no later part of the plan ran.
 */

type RunStatus = 'pending' | 'running' | 'waiting_approval' | 'blocked' | 'completed'
type StepStatus = 'pending' | 'running' | 'completed' | 'awaiting_approval' | 'failed' | 'skipped'
type StepKind = 'action' | 'checkpoint'

interface AgentDefinition {
  id: string
  key: string
  name: string
  description: string
  capabilities: string[]
  enabled: boolean
  created_at: string
  updated_at: string
}

interface CapabilitySpec {
  key: string
  label: string
  description: string
  requires_approval: boolean
}

interface GoalStepView {
  key: string
  title: string
  capability: string
  capability_label: string
  kind: StepKind
  agent_key?: string
  depends_on: string[]
}

interface GoalView {
  key: string
  title: string
  summary: string
  defaults: Record<string, string>
  steps: GoalStepView[]
  step_count: number
}

interface AgentStep {
  id: string
  run_id: string
  sequence: number
  layer: number
  key: string
  title: string
  agent_id: string
  agent_key: string
  agent_name: string
  capability: string
  kind: StepKind
  depends_on: string[]
  status: StepStatus
  attempts: number
  result?: string
  last_error?: string
  approved_by?: string
  approved_at?: string
  approval_note?: string
  started_at?: string
  finished_at?: string
  updated_at: string
}

interface AgentRun {
  id: string
  code: string
  goal_key: string
  goal_title: string
  status: RunStatus
  total_steps: number
  completed_steps: number
  current_sequence: number
  blocked_step_key?: string
  block_reason?: string
  values: Record<string, string>
  requested_by?: string
  started_at?: string
  finished_at?: string
  created_at: string
  updated_at: string
  steps_completed?: number
  steps_awaiting?: number
  steps_failed?: number
  steps_skipped?: number
}

interface AgentRunDetail extends AgentRun {
  steps: AgentStep[]
  can_approve: boolean
  awaiting_step_id?: string
  next_action: string
}

const AGENTS_KEY = ['agents'] as const
const CAPABILITIES_KEY = ['agent-capabilities'] as const
const GOALS_KEY = ['agent-goals'] as const
const RUNS_KEY = ['agent-runs'] as const
const runKey = (id: string) => ['agent-run', id] as const

const runLabel: Record<RunStatus, string> = {
  pending: 'รอเริ่ม',
  running: 'กำลังดำเนินการ',
  waiting_approval: 'รออนุมัติจากเจ้าหน้าที่',
  blocked: 'หยุดเพราะขั้นตอนล้มเหลว',
  completed: 'สำเร็จครบทุกขั้น',
}

const runTone: Record<RunStatus, string> = {
  pending: 'dim',
  running: 'sev-medium',
  waiting_approval: 'sev-high',
  blocked: 'sev-critical',
  completed: 'sev-low',
}

const stepLabel: Record<StepStatus, string> = {
  pending: 'รอดำเนินการ',
  running: 'กำลังทำ',
  completed: 'สำเร็จ',
  awaiting_approval: 'รออนุมัติ',
  failed: 'ล้มเหลว',
  skipped: 'ข้าม',
}

const stepTone: Record<StepStatus, string> = {
  pending: 'dim',
  running: 'sev-medium',
  completed: 'sev-low',
  awaiting_approval: 'sev-high',
  failed: 'sev-critical',
  skipped: 'dim',
}

const valueLabels: Record<string, string> = {
  finding_count: 'จำนวนข้อค้นพบ',
  document_count: 'จำนวนเอกสาร',
  invoice_no: 'เลขที่ใบแจ้งหนี้',
  recipient: 'ผู้รับการแจ้งเตือน',
  case_title: 'ชื่อเคส',
  summary_title: 'หัวข้อสรุป',
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function capabilitiesOf(agent: AgentDefinition, catalog: CapabilitySpec[]): string[] {
  const labels = new Map(catalog.map((item) => [item.key, item.label]))
  return agent.capabilities.map((key) => labels.get(key) ?? key)
}

function amountFromBaht(raw: string): number {
  const baht = Number(raw.replace(/,/g, ''))
  if (!Number.isFinite(baht) || baht <= 0) return 0
  return Math.round(baht * 100)
}

export function Agents() {
  const qc = useQueryClient()
  const { me } = useAuth()
  const role: Role | undefined = me?.role
  const canDefine = role === 'owner' || role === 'admin'
  const canStart = role !== undefined && role !== 'reviewer' && role !== 'client_viewer'
  const canApprove = role === 'owner' || role === 'admin' || role === 'audit_manager'

  const [selectedID, setSelectedID] = useState<string | null>(null)
  const [agentKey, setAgentKey] = useState('')
  const [agentName, setAgentName] = useState('')
  const [agentDescription, setAgentDescription] = useState('')
  const [agentCapabilities, setAgentCapabilities] = useState<string[]>([])
  const [agentEnabled, setAgentEnabled] = useState(true)
  const [agentError, setAgentError] = useState<string | null>(null)

  const [goalKey, setGoalKey] = useState('')
  const [values, setValues] = useState<Record<string, string>>({})
  const [amountBaht, setAmountBaht] = useState('')
  const [approvalRef, setApprovalRef] = useState('')
  const [runError, setRunError] = useState<string | null>(null)
  const [approveNote, setApproveNote] = useState('')
  const [approveError, setApproveError] = useState<string | null>(null)

  const agents = useQuery({
    queryKey: AGENTS_KEY,
    queryFn: () => api<AgentDefinition[]>('/v1/agents?limit=200'),
    staleTime: 30_000,
  })
  const capabilities = useQuery({
    queryKey: CAPABILITIES_KEY,
    queryFn: () => api<CapabilitySpec[]>('/v1/agents/capabilities'),
    staleTime: 5 * 60_000,
  })
  const goals = useQuery({
    queryKey: GOALS_KEY,
    queryFn: () => api<GoalView[]>('/v1/agents/goals'),
    staleTime: 5 * 60_000,
  })
  const runs = useQuery({
    queryKey: RUNS_KEY,
    queryFn: () => api<AgentRun[]>('/v1/agent-runs?limit=100'),
    refetchInterval: 15_000,
  })
  const detail = useQuery({
    queryKey: selectedID ? runKey(selectedID) : ['agent-run', 'none'],
    queryFn: async () => (await api<{ run: AgentRunDetail }>(`/v1/agent-runs/${selectedID}`)).run,
    enabled: selectedID !== null,
  })

  const capabilitiesByKey = useMemo(
    () => new Map((capabilities.data ?? []).map((item) => [item.key, item])),
    [capabilities.data],
  )
  const selectedGoal = useMemo(
    () => (goals.data ?? []).find((goal) => goal.key === goalKey),
    [goals.data, goalKey],
  )
  const runRows = runs.data ?? []

  useEffect(() => {
    if (!goalKey && goals.data && goals.data.length > 0) setGoalKey(goals.data[0].key)
  }, [goals.data, goalKey])

  useEffect(() => {
    if (!selectedGoal) return
    const next: Record<string, string> = {}
    for (const [key, value] of Object.entries(selectedGoal.defaults)) {
      if (key !== 'amount_satang') next[key] = value
    }
    setValues(next)
    const satang = Number(selectedGoal.defaults.amount_satang ?? '0')
    setAmountBaht(Number.isFinite(satang) && satang > 0 ? (satang / 100).toFixed(2) : '')
    setApprovalRef('')
    setRunError(null)
  }, [selectedGoal?.key])

  useEffect(() => {
    if (!selectedID && runRows.length > 0) setSelectedID(runRows[0].id)
  }, [runRows, selectedID])

  const createAgent = useMutation({
    mutationFn: () =>
      apiWrite<{ agent: AgentDefinition }>('/v1/agents', 'POST', {
        key: agentKey.trim().toLowerCase(),
        name: agentName.trim(),
        description: agentDescription.trim(),
        capabilities: agentCapabilities,
        enabled: agentEnabled,
      }),
    onSuccess: () => {
      setAgentError(null)
      setAgentKey('')
      setAgentName('')
      setAgentDescription('')
      setAgentCapabilities([])
      setAgentEnabled(true)
      void qc.invalidateQueries({ queryKey: AGENTS_KEY })
    },
    onError: (err) => setAgentError(errorText(err, 'กำหนดเอเจนต์ไม่สำเร็จ')),
  })

  const startRun = useMutation({
    mutationFn: (body: { goal: string; values: Record<string, string> }) =>
      apiWrite<{ run: AgentRunDetail }>('/v1/agent-runs', 'POST', body),
    onSuccess: (res) => {
      setRunError(null)
      setSelectedID(res.run.id)
      qc.setQueryData(runKey(res.run.id), res.run)
      void qc.invalidateQueries({ queryKey: RUNS_KEY })
    },
    onError: (err) => setRunError(errorText(err, 'เริ่มแผนงานไม่สำเร็จ')),
  })

  const approve = useMutation({
    mutationFn: (input: { runID: string; stepID: string; note: string }) =>
      apiWrite<{ run: AgentRunDetail }>(
        `/v1/agent-runs/${input.runID}/steps/${input.stepID}/approve`,
        'POST',
        { note: input.note },
      ),
    onSuccess: (res) => {
      setApproveError(null)
      setApproveNote('')
      qc.setQueryData(runKey(res.run.id), res.run)
      void qc.invalidateQueries({ queryKey: RUNS_KEY })
    },
    onError: (err) => setApproveError(errorText(err, 'อนุมัติจุดตรวจสอบไม่สำเร็จ')),
  })

  const selected = detail.data
  const waitingCount = runRows.filter((row) => row.status === 'waiting_approval').length
  const blockedCount = runRows.filter((row) => row.status === 'blocked').length
  const completedCount = runRows.filter((row) => row.status === 'completed').length

  function toggleCapability(key: string) {
    setAgentCapabilities((current) =>
      current.includes(key) ? current.filter((item) => item !== key) : [...current, key],
    )
  }

  function submitAgent(event: FormEvent) {
    event.preventDefault()
    setAgentError(null)
    if (!agentKey.trim()) {
      setAgentError('ต้องระบุรหัสเอเจนต์')
      return
    }
    if (!agentName.trim()) {
      setAgentError('ต้องระบุชื่อเอเจนต์')
      return
    }
    if (agentCapabilities.length === 0) {
      setAgentError('เลือกความสามารถอย่างน้อยหนึ่งรายการ')
      return
    }
    createAgent.mutate()
  }

  function submitRun(event: FormEvent) {
    event.preventDefault()
    setRunError(null)
    if (!selectedGoal) {
      setRunError('เลือกเป้าหมายก่อนเริ่มแผน')
      return
    }
    const payload: Record<string, string> = {}
    for (const [key, value] of Object.entries(values)) {
      if (key === 'amount_satang') continue
      if (value.trim() !== '') payload[key] = value.trim()
    }
    if (selectedGoal.defaults.amount_satang !== undefined) {
      const satang = amountFromBaht(amountBaht)
      if (satang <= 0) {
        setRunError('ยอดเงินต้องมากกว่าศูนย์บาท')
        return
      }
      payload.amount_satang = String(satang)
    }
    if (approvalRef.trim() !== '') payload.approval_ref = approvalRef.trim()
    startRun.mutate({ goal: selectedGoal.key, values: payload })
  }

  const fieldKeys = Object.keys(selectedGoal?.defaults ?? {}).filter(
    (key) => key !== 'amount_satang' && key !== 'approval_ref',
  )

  return (
    <div className="stack" style={{ gap: 18 }}>
      <PageHead
        title="เอเจนต์และแผนงานอัตโนมัติ"
        sub="ประกาศความสามารถของเอเจนต์ แล้วเริ่มแผนงานที่มีลำดับพึ่งพาชัดเจน แผนจะหยุดที่จุดตรวจสอบเพื่อรอการอนุมัติจากเจ้าหน้าที่ และจะหยุดทั้งแผนทันทีเมื่อขั้นตอนใดล้มเหลว โดยไม่ดำเนินขั้นตอนที่เหลือ"
      />

      <div className="grid-4">
        <Stat label="เอเจนต์ที่ประกาศ" value={number(agents.data?.length ?? 0)} unit="ตัว" foot="นับเฉพาะองค์กรนี้" />
        <Stat label="รอบทั้งหมด" value={number(runRows.length)} unit="รอบ" foot="เรียงจากใหม่ไปเก่า" />
        <Stat
          label="รออนุมัติ"
          value={number(waitingCount)}
          unit="รอบ"
          foot={blockedCount > 0 ? `หยุดเพราะขั้นตอนล้มเหลว ${number(blockedCount)} รอบ` : 'ไม่มีรอบที่ถูกบล็อก'}
        />
        <Stat label="สำเร็จครบ" value={number(completedCount)} unit="รอบ" foot="ทุกขั้นตอนรวมจุดตรวจสอบผ่านแล้ว" />
      </div>

      <div className="grid-main">
        <div className="stack" style={{ gap: 16 }}>
          <div className="card">
            <div className="card-pad" style={{ paddingBottom: 8 }}>
              <div className="row" style={{ marginBottom: 4 }}>
                <span className="card-label">เอเจนต์และความสามารถ</span>
                <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                  ความสามารถเป็นรายการปิด ระบบไม่รับค่าที่อยู่นอกรายการ
                </span>
              </div>
            </div>
            {agents.isLoading ? (
              <div className="card-pad stack" style={{ gap: 8 }}>
                <Skel h={14} />
                <Skel h={14} w="72%" />
              </div>
            ) : (agents.data ?? []).length === 0 ? (
              <div className="card-pad">
                <Empty
                  title="ยังไม่มีเอเจนต์"
                  body="กำหนดเอเจนต์อย่างน้อยหนึ่งตัวพร้อมความสามารถก่อนเริ่มแผน ระบบจะไม่สร้างเอเจนต์หรือให้สิทธิ์โดยอัตโนมัติ"
                />
              </div>
            ) : (
              <div className="table-wrap" style={{ border: 0, borderRadius: 0 }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>เอเจนต์</th>
                      <th>ความสามารถ</th>
                      <th>สถานะ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(agents.data ?? []).map((agent) => (
                      <tr key={agent.id}>
                        <td>
                          <div style={{ fontWeight: 500 }}>{agent.name}</div>
                          <div className="mono dim">{agent.key}</div>
                          {agent.description ? (
                            <div className="dim" style={{ fontSize: 11.5, marginTop: 3 }}>
                              {agent.description}
                            </div>
                          ) : null}
                        </td>
                        <td>
                          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                            {capabilitiesOf(agent, capabilities.data ?? []).map((label) => (
                              <span className="chip" key={label}>
                                {label}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td>
                          <span className={agent.enabled ? 'sev sev-low' : 'sev dim'}>
                            <i className="sev-dot" />
                            {agent.enabled ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {canDefine ? (
            <form className="card card-pad" onSubmit={submitAgent}>
              <div className="row" style={{ marginBottom: 4 }}>
                <span className="card-label">กำหนดเอเจนต์ใหม่</span>
                <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                  เฉพาะเจ้าของและผู้ดูแลระบบ
                </span>
              </div>
              <div className="grid-2" style={{ gap: 14 }}>
                <label className="stack" style={{ gap: 6 }}>
                  <span className="dim" style={{ fontSize: 11.5 }}>รหัสเอเจนต์</span>
                  <input
                    className="input mono"
                    value={agentKey}
                    onChange={(event) => setAgentKey(event.target.value)}
                    placeholder="case_reader"
                    maxLength={48}
                  />
                </label>
                <label className="stack" style={{ gap: 6 }}>
                  <span className="dim" style={{ fontSize: 11.5 }}>ชื่อที่แสดง</span>
                  <input
                    className="input"
                    value={agentName}
                    onChange={(event) => setAgentName(event.target.value)}
                    placeholder="ผู้ช่วยอ่านสำนวน"
                    maxLength={300}
                  />
                </label>
              </div>
              <label className="stack" style={{ gap: 6, marginTop: 14 }}>
                <span className="dim" style={{ fontSize: 11.5 }}>คำอธิบาย (ไม่บังคับ)</span>
                <input
                  className="input"
                  value={agentDescription}
                  onChange={(event) => setAgentDescription(event.target.value)}
                  placeholder="อ่านข้อค้นพบและเอกสารที่เกี่ยวข้อง"
                  maxLength={500}
                />
              </label>

              <div className="field">
                <label>ความสามารถที่ประกาศ</label>
                <div className="stack" style={{ gap: 8 }}>
                  {(capabilities.data ?? []).map((item) => (
                    <label className="row" key={item.key} style={{ gap: 8, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={agentCapabilities.includes(item.key)}
                        onChange={() => toggleCapability(item.key)}
                      />
                      <span style={{ fontSize: 12.5 }}>{item.label}</span>
                      <span className="mono dim" style={{ fontSize: 11 }}>{item.key}</span>
                      {item.requires_approval ? <span className="chip gold">ต้องอนุมัติ</span> : null}
                    </label>
                  ))}
                </div>
              </div>

              <div className="row" style={{ marginTop: 16 }}>
                <label className="row" style={{ gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={agentEnabled}
                    onChange={(event) => setAgentEnabled(event.target.checked)}
                  />
                  <span style={{ fontSize: 12.5 }}>เปิดใช้งานทันที</span>
                </label>
                <button className="btn btn-primary" type="submit" style={{ marginLeft: 'auto' }} disabled={createAgent.isPending}>
                  {createAgent.isPending ? 'กำลังบันทึก…' : 'บันทึกเอเจนต์'}
                </button>
              </div>
              {agentError ? (
                <p className="dim" style={{ color: 'var(--color-critical)', fontSize: 12, marginTop: 10 }}>
                  {agentError}
                </p>
              ) : null}
            </form>
          ) : null}

          <div className="card">
            <div className="card-pad" style={{ paddingBottom: 8 }}>
              <span className="card-label">รอบการทำงาน</span>
            </div>
            {runs.isLoading ? (
              <div className="card-pad stack" style={{ gap: 8 }}>
                <Skel h={14} />
                <Skel h={14} w="64%" />
              </div>
            ) : runRows.length === 0 ? (
              <div className="card-pad">
                <Empty title="ยังไม่มีรอบการทำงาน" body="เริ่มแผนจากเป้าหมายที่กำหนดไว้ ระบบจะบันทึกทุกขั้นตอนและสถานะไว้ที่นี่" />
              </div>
            ) : (
              <div className="table-wrap" style={{ border: 0, borderRadius: 0 }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>รอบ</th>
                      <th>เป้าหมาย</th>
                      <th>ความคืบหน้า</th>
                      <th>สถานะ</th>
                      <th>เริ่มเมื่อ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runRows.map((row) => (
                      <tr
                        key={row.id}
                        onClick={() => setSelectedID(row.id)}
                        style={{ cursor: 'pointer', background: row.id === selectedID ? 'var(--color-wash)' : undefined }}
                      >
                        <td className="mono">{row.code}</td>
                        <td>{row.goal_title}</td>
                        <td className="num tnum">
                          {number(row.completed_steps)} / {number(row.total_steps)}
                        </td>
                        <td>
                          <span className={`sev ${runTone[row.status]}`}>
                            <i className="sev-dot" />
                            {runLabel[row.status]}
                          </span>
                        </td>
                        <td className="dim" style={{ fontSize: 11.5 }}>{thaiDateTime(row.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="stack" style={{ gap: 16 }}>
          {canStart ? (
            <form className="card card-pad" onSubmit={submitRun}>
              <div className="row" style={{ marginBottom: 12 }}>
                <span className="card-label">เริ่มแผนงานใหม่</span>
              </div>
              <label className="stack" style={{ gap: 6 }}>
                <span className="dim" style={{ fontSize: 11.5 }}>เป้าหมาย</span>
                <select className="input" value={goalKey} onChange={(event) => setGoalKey(event.target.value)}>
                  {(goals.data ?? []).map((goal) => (
                    <option key={goal.key} value={goal.key}>
                      {goal.title}
                    </option>
                  ))}
                </select>
              </label>

              {selectedGoal ? (
                <>
                  <p className="dim" style={{ fontSize: 12, lineHeight: 1.7, marginTop: 10 }}>
                    {selectedGoal.summary}
                  </p>
                  <div className="stack" style={{ gap: 6, marginTop: 10 }}>
                    {selectedGoal.steps.map((step, index) => (
                      <div className="row" key={step.key} style={{ gap: 8, alignItems: 'flex-start' }}>
                        <span className="mono dim" style={{ minWidth: 18 }}>{index + 1}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12.5 }}>{step.title}</div>
                          <div className="dim" style={{ fontSize: 11 }}>
                            {step.capability_label}
                            {step.depends_on.length > 0 ? ` · ขึ้นกับ ${step.depends_on.join(', ')}` : ' · เริ่มได้ทันที'}
                          </div>
                        </div>
                        {step.kind === 'checkpoint' ? <span className="chip gold">จุดอนุมัติ</span> : null}
                      </div>
                    ))}
                  </div>

                  {fieldKeys.map((key) => (
                    <label className="stack" key={key} style={{ gap: 6, marginTop: 12 }}>
                      <span className="dim" style={{ fontSize: 11.5 }}>{valueLabels[key] ?? key}</span>
                      <input
                        className="input"
                        value={values[key] ?? ''}
                        onChange={(event) => setValues((current) => ({ ...current, [key]: event.target.value }))}
                        maxLength={500}
                      />
                    </label>
                  ))}

                  {selectedGoal.defaults.amount_satang !== undefined ? (
                    <label className="stack" style={{ gap: 6, marginTop: 12 }}>
                      <span className="dim" style={{ fontSize: 11.5 }}>ยอดปรับปรุง (บาท)</span>
                      <input
                        className="input tnum"
                        inputMode="decimal"
                        value={amountBaht}
                        onChange={(event) => setAmountBaht(event.target.value)}
                        placeholder="2500.00"
                      />
                      <span className="dim" style={{ fontSize: 11 }}>
                        วงเงินอนุมัติ 50,000.00 บาท ยอดที่เกินวงเงินต้องมีเลขอ้างอิงการอนุมัติ มิฉะนั้นขั้นตอนบันทึกจะหยุดทั้งแผน
                      </span>
                    </label>
                  ) : null}

                  <label className="stack" style={{ gap: 6, marginTop: 12 }}>
                    <span className="dim" style={{ fontSize: 11.5 }}>เลขอ้างอิงการอนุมัติ (ถ้ามียอดเกินวงเงิน)</span>
                    <input
                      className="input"
                      value={approvalRef}
                      onChange={(event) => setApprovalRef(event.target.value)}
                      placeholder="APR-2026-0042"
                      maxLength={120}
                    />
                  </label>
                </>
              ) : null}

              <button className="btn btn-primary" type="submit" style={{ marginTop: 16, width: '100%' }} disabled={startRun.isPending}>
                {startRun.isPending ? 'กำลังวางแผนและเริ่ม…' : 'วางแผนและเริ่มรอบใหม่'}
              </button>
              {runError ? (
                <p className="dim" style={{ color: 'var(--color-critical)', fontSize: 12, marginTop: 10 }}>
                  {runError}
                </p>
              ) : null}
            </form>
          ) : null}

          <div className="card card-pad">
            <div className="row" style={{ marginBottom: 10 }}>
              <span className="card-label">รายละเอียดรอบ</span>
              {selected ? (
                <span className={`sev ${runTone[selected.status]}`} style={{ marginLeft: 'auto' }}>
                  <i className="sev-dot" />
                  {runLabel[selected.status]}
                </span>
              ) : null}
            </div>

            {!selected ? (
              <Empty title="ยังไม่ได้เลือกรอบ" body="เลือกรอบจากตารางด้านซ้ายเพื่อดูลำดับขั้นตอน ผลลัพธ์ และจุดที่ต้องอนุมัติ" />
            ) : (
              <div className="stack" style={{ gap: 12 }}>
                <div>
                  <div className="mono" style={{ fontSize: 12.5 }}>{selected.code}</div>
                  <div style={{ marginTop: 4 }}>{selected.goal_title}</div>
                  <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
                    {number(selected.completed_steps)} จาก {number(selected.total_steps)} ขั้นตอน · เริ่ม {selected.started_at ? thaiDateTime(selected.started_at) : 'ยังไม่เริ่ม'}
                  </div>
                </div>

                {selected.status === 'waiting_approval' ? (
                  <div className="card card-pad" style={{ borderColor: 'rgba(199,123,43,0.4)' }}>
                    <div style={{ fontSize: 12.5, color: 'var(--color-amber)' }}>
                      แผนหยุดที่จุดตรวจสอบและรอการอนุมัติจากเจ้าหน้าที่ ขั้นตอนถัดไปจะไม่ทำงานจนกว่าจะมีผู้อนุมัติ
                    </div>
                  </div>
                ) : null}

                {selected.status === 'blocked' ? (
                  <div className="card card-pad" style={{ borderColor: 'rgba(166,58,74,0.5)' }}>
                    <div style={{ fontSize: 12.5, color: 'var(--color-critical)' }}>
                      แผนหยุดที่ขั้นตอน {selected.blocked_step_key}
                    </div>
                    <div className="dim" style={{ fontSize: 11.5, marginTop: 5, lineHeight: 1.6 }}>
                      {selected.block_reason}
                    </div>
                  </div>
                ) : null}

                {selected.can_approve && canApprove && selected.awaiting_step_id ? (
                  <div className="stack" style={{ gap: 8 }}>
                    <label className="stack" style={{ gap: 6 }}>
                      <span className="dim" style={{ fontSize: 11.5 }}>หมายเหตุการอนุมัติ (ไม่บังคับ)</span>
                      <input
                        className="input"
                        value={approveNote}
                        onChange={(event) => setApproveNote(event.target.value)}
                        placeholder="อนุมัติให้ดำเนินการต่อ"
                        maxLength={500}
                      />
                    </label>
                    <button
                      className="btn btn-primary"
                      type="button"
                      disabled={approve.isPending}
                      onClick={() =>
                        approve.mutate({
                          runID: selected.id,
                          stepID: selected.awaiting_step_id ?? '',
                          note: approveNote.trim(),
                        })
                      }
                    >
                      {approve.isPending ? 'กำลังอนุมัติ…' : 'อนุมัติจุดตรวจสอบและดำเนินการต่อ'}
                    </button>
                    {approveError ? (
                      <p className="dim" style={{ color: 'var(--color-critical)', fontSize: 12 }}>
                        {approveError}
                      </p>
                    ) : null}
                  </div>
                ) : null}

                <div className="stack" style={{ gap: 8 }}>
                  {selected.steps.map((step) => (
                    <div className="card card-pad" key={step.id} style={{ padding: 14 }}>
                      <div className="row" style={{ alignItems: 'flex-start' }}>
                        <span className="mono dim" style={{ minWidth: 18 }}>{step.sequence}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12.5, fontWeight: 500 }}>{step.title}</div>
                          <div className="dim" style={{ fontSize: 11, marginTop: 3 }}>
                            {step.agent_name} · {step.agent_key}
                          </div>
                          <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                            <span className="chip">{capabilitiesByKey.get(step.capability)?.label ?? step.capability}</span>
                            {step.kind === 'checkpoint' ? <span className="chip gold">จุดอนุมัติ</span> : null}
                            {step.depends_on.length > 0 ? (
                              <span className="chip">ขึ้นกับ {step.depends_on.join(', ')}</span>
                            ) : null}
                          </div>
                        </div>
                        <span className={`sev ${stepTone[step.status]}`}>
                          <i className="sev-dot" />
                          {stepLabel[step.status]}
                        </span>
                      </div>

                      {step.result ? (
                        <div className="dim" style={{ fontSize: 11.5, marginTop: 8, lineHeight: 1.6 }}>
                          {step.result}
                        </div>
                      ) : null}
                      {step.last_error ? (
                        <div style={{ fontSize: 11.5, marginTop: 8, color: 'var(--color-critical)', lineHeight: 1.6 }}>
                          {step.last_error}
                        </div>
                      ) : null}
                      {step.approved_at ? (
                        <div className="dim" style={{ fontSize: 11, marginTop: 8 }}>
                          อนุมัติเมื่อ {thaiDateTime(step.approved_at)}
                          {step.approval_note ? ` · ${step.approval_note}` : ''}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
